import { Link } from '@/components/ui/link';
import { Button, type ButtonProps } from '@/components/ui/button';
import { Section, Title } from '@/components/ui/layout';
import { LoadingState } from '@/components/ui/loading';
import { useState, type ReactNode } from 'react';

import { useLanguage } from '@/i18n/language-context';
import { SiteShell } from '@/components/site-shell';
import { usePageMetadata } from '@/i18n/use-page-metadata';
import { describeError, useDonorCopy } from './copy';
import { DonorError } from './http';
import { useBrowserCapabilities } from './use-browser-capabilities';
import { BrowserHandoff } from './browser-handoff';
export function DonorLayout({ children }: { children: ReactNode }): JSX.Element {
  usePageMetadata();
  const supported = useBrowserCapabilities();
  return (
    <SiteShell>
      <main id="main-content" tabIndex={-1} className="giving-main ui-stack">
        {!supported && <BrowserHandoff />}
        {children}
      </main>
    </SiteShell>
  );
}
export function ErrorNotice({ error }: { error: unknown }): JSX.Element | null {
  const { t } = useDonorCopy();
  if (error instanceof DonorError && error.code === 'GUEST_SESSION_UNAVAILABLE')
    return <BrowserHandoff />;
  return error ? (
    <p className="giving-error" role="alert">
      {describeError(error, t)}
    </p>
  ) : null;
}
export function DonorLoadingPage(): JSX.Element {
  const { t } = useDonorCopy();
  return (
    <DonorLayout>
      <Section surface="card" flow>
        <LoadingState label={t.loading} kind="summary" count={1} />
      </Section>
    </DonorLayout>
  );
}
export function AsyncButton({
  action,
  children,
  ...props
}: Omit<ButtonProps, 'onClick' | 'type'> & { action: () => Promise<unknown> }): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>();
  return (
    <>
      <Button
        {...props}
        type="button"
        loading={busy}
        disabled={busy || props.disabled}
        onClick={() => {
          setBusy(true);
          setError(undefined);
          void action()
            .catch(setError)
            .finally(() => setBusy(false));
        }}
      >
        {children}
      </Button>
      <ErrorNotice error={error} />
    </>
  );
}
export function SignInLink({ returnTo }: { returnTo?: string }): JSX.Element {
  const { t } = useDonorCopy();
  useLanguage();
  return (
    <Link
      appearance="secondary"

      to={`/sign-in${returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : ''}`}
    >
      {t.signIn}
    </Link>
  );
}

export function MissingDonorPage(): JSX.Element {
  const { t } = useDonorCopy();
  return (
    <DonorLayout>
      <Title level={1}>{t.missingPage}</Title>
      <Link to="/">{t.browse}</Link>
    </DonorLayout>
  );
}
